import { useCallback, useEffect } from 'react';

/**
 * reCAPTCHA v3. The script is loaded only on the pages that call this hook, so the rest of the
 * site stays free of it. With no site key (local dev) it returns an empty token, which the
 * backend accepts outside production.
 */

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

interface Grecaptcha {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
}

let loading: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (!loading) {
    loading = new Promise<void>((resolve, reject) => {
      const el = document.createElement('script');
      el.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
      el.async = true;
      el.onload = () => resolve();
      el.onerror = () => {
        loading = null;
        reject(new Error('Could not load reCAPTCHA'));
      };
      document.head.appendChild(el);
    });
  }
  return loading;
}

export function useRecaptcha(): (action: 'signup' | 'login_link') => Promise<string> {
  useEffect(() => {
    if (SITE_KEY) loadScript().catch(() => undefined);
  }, []);

  return useCallback(async (action) => {
    if (!SITE_KEY) return '';
    await loadScript();
    const g = (window as unknown as { grecaptcha: Grecaptcha }).grecaptcha;
    return new Promise<string>((resolve, reject) => {
      g.ready(() => g.execute(SITE_KEY, { action }).then(resolve, reject));
    });
  }, []);
}
